import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import api from '../services/api.ts';
import FileUpload, { FileAttachment } from '../components/FileUpload.tsx';
import { formatCurrencyInput, parseCurrency, applyCurrencyMask, formatCurrency } from '../utils/currency.ts';
import {
  ArrowLeftIcon,
  BuildingOfficeIcon,
  TruckIcon,
  CubeIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

// Types
interface AssetFormData {
  description: string;
  type: 'real_estate' | 'other';
  acquisitionValue: number;
  amountPaid: number;
  currentValue: number;
  acquisitionDate: string;
  attachments: FileAttachment[];
  details: {
    // Propriedades para imóveis
    address?: string;
    area?: number;
    rooms?: number;
    bathrooms?: number;
    garage?: boolean;
    furnished?: boolean;
    registrationNumber?: string;
    // Propriedades para veículos
    make?: string;
    model?: string;
    year?: number;
    licensePlate?: string;
    color?: string;
    // Propriedades gerais
    notes?: string;
  };
}

const CreateAsset: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<AssetFormData>({
    description: '',
    type: 'real_estate',
    acquisitionValue: 0,
    amountPaid: 0,
    currentValue: 0,
    acquisitionDate: new Date().toISOString().split('T')[0],
    attachments: [],
    details: {},
  });

  // Estados para os valores formatados dos inputs
  const [acquisitionValueDisplay, setAcquisitionValueDisplay] = useState<string>('');
  const [amountPaidDisplay, setAmountPaidDisplay] = useState<string>('');
  const [currentValueDisplay, setCurrentValueDisplay] = useState<string>('');

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Mutation para criar ativo
  const createAssetMutation = useMutation(
    async (data: AssetFormData) => {
      let uploadedFiles: any[] = [];
      
      // Primeiro, fazer upload dos arquivos se existirem
      if (data.attachments && data.attachments.length > 0) {
        const formData = new FormData();
        data.attachments.forEach((attachment: any) => {
          formData.append('files', attachment.file);
        });

        const uploadResponse = await api.post('/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (uploadResponse.data.success) {
          uploadedFiles = uploadResponse.data.data.files.map((file: any, index: number) => ({
            id: Date.now() + index,
            name: file.originalName,
            filename: file.filename,
            url: file.url,
            size: file.size,
            mimetype: file.mimetype,
            type: data.attachments[index].type,
            category: data.attachments[index].category,
          }));
        }
      }

      // Criar o ativo com os anexos já salvos
      const assetData = {
        ...data,
        currentValue: data.currentValue || null,
        attachments: uploadedFiles, // Substituir pelos arquivos já salvos
      };

      const response = await api.post('/assets', assetData);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('assets');
        navigate('/assets');
      },
      onError: (error: any) => {
        console.error('Erro ao criar ativo:', error);
      },
    }
  );

  const handleInputChange = (field: keyof AssetFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  // Função específica para lidar com campos de valor monetário
  const handleCurrencyChange = (
    field: 'acquisitionValue' | 'currentValue' | 'amountPaid',
    value: string
  ) => {
    const maskedValue = applyCurrencyMask(value);
    const numericValue = parseCurrency(maskedValue);

    if (field === 'acquisitionValue') {
      setAcquisitionValueDisplay(maskedValue);
    } else if (field === 'amountPaid') {
      setAmountPaidDisplay(maskedValue);
    } else {
      setCurrentValueDisplay(maskedValue);
    }

    setFormData(prev => ({
      ...prev,
      [field]: numericValue,
    }));

    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleDetailsChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      details: {
        ...prev.details,
        [field]: value,
      },
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.description.trim()) {
      newErrors.description = 'Descrição é obrigatória';
    }

    if (formData.acquisitionValue <= 0) {
      newErrors.acquisitionValue = 'Valor de aquisição deve ser maior que zero';
    }

    if (formData.currentValue <= 0) {
      newErrors.currentValue = 'Valor atual deve ser maior que zero';
    }

    if (!formData.acquisitionDate) {
      newErrors.acquisitionDate = 'Data de aquisição é obrigatória';
    }

    // Validações específicas por tipo
    if (formData.type === 'real_estate') {
      if (!formData.details.address?.trim()) {
        newErrors.address = 'Endereço é obrigatório para imóveis';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      createAssetMutation.mutate(formData);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'real_estate':
        return <BuildingOfficeIcon className="h-5 w-5" />;
      case 'vehicle':
        return <TruckIcon className="h-5 w-5" />;
      default:
        return <CubeIcon className="h-5 w-5" />;
    }
  };

  const renderTypeSpecificFields = () => {
    switch (formData.type) {
      case 'real_estate':
        return (
          <div className="space-y-6">
            <h4 className="text-lg font-medium text-gray-900 flex items-center">
              <BuildingOfficeIcon className="h-5 w-5 mr-2 text-primary-600" />
              Detalhes do Imóvel
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                  Endereço *
                </label>
                <input
                  id="address"
                  type="text"
                  value={formData.details.address || ''}
                  onChange={e => handleDetailsChange('address', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.address ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Rua, número, bairro, cidade"
                />
                {errors.address && <p className="mt-1 text-sm text-red-600">{errors.address}</p>}
              </div>

              <div>
                <label htmlFor="area" className="block text-sm font-medium text-gray-700 mb-2">
                  Área (m²)
                </label>
                <input
                  id="area"
                  type="number"
                  value={formData.details.area || ''}
                  onChange={e => handleDetailsChange('area', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0"
                  min="0"
                  step="0.01"
                />
              </div>

              <div>
                <label
                  htmlFor="registrationNumber"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Número de Registro
                </label>
                <input
                  id="registrationNumber"
                  type="text"
                  value={formData.details.registrationNumber || ''}
                  onChange={e => handleDetailsChange('registrationNumber', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Matrícula do imóvel"
                />
              </div>

              <div>
                <label htmlFor="rooms" className="block text-sm font-medium text-gray-700 mb-2">
                  Quartos
                </label>
                <input
                  id="rooms"
                  type="number"
                  value={formData.details.rooms || ''}
                  onChange={e => handleDetailsChange('rooms', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0"
                  min="0"
                />
              </div>

              <div>
                <label htmlFor="bathrooms" className="block text-sm font-medium text-gray-700 mb-2">
                  Banheiros
                </label>
                <input
                  id="bathrooms"
                  type="number"
                  value={formData.details.bathrooms || ''}
                  onChange={e => handleDetailsChange('bathrooms', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0"
                  min="0"
                />
              </div>

              <div className="flex items-center space-x-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.details.garage || false}
                    onChange={e => handleDetailsChange('garage', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Garagem</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.details.furnished || false}
                    onChange={e => handleDetailsChange('furnished', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Mobiliado</span>
                </label>
              </div>
            </div>
          </div>
        );



      default:
        return (
          <div className="space-y-6">
            <h4 className="text-lg font-medium text-gray-900 flex items-center">
              <CubeIcon className="h-5 w-5 mr-2 text-purple-600" />
              Detalhes Adicionais
            </h4>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/assets')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Voltar para Ativos
          </button>

          <h1 className="text-3xl font-bold text-gray-900">Criar Novo Ativo</h1>
          <p className="text-gray-600 mt-2">Adicione um novo ativo ao seu patrimônio</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Informações Básicas */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <DocumentTextIcon className="h-6 w-6 mr-2 text-blue-600" />
              Informações Básicas
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label
                  htmlFor="description"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Descrição *
                </label>
                <input
                  id="description"
                  type="text"
                  value={formData.description}
                  onChange={e => handleInputChange('description', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.description ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ex: Apartamento Centro, Casa Praia, etc."
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                )}
              </div>

              <div>
                <label htmlFor="assetType" className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Ativo *
                </label>
                <select
                  id="assetType"
                  value={formData.type}
                  onChange={e => handleInputChange('type', e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="real_estate">Imóvel</option>
                  <option value="other">Outros</option>
                </select>
              </div>

              <div>
                <label
                  htmlFor="acquisitionDate"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Data de Aquisição *
                </label>
                <input
                  id="acquisitionDate"
                  type="date"
                  value={formData.acquisitionDate}
                  onChange={e => handleInputChange('acquisitionDate', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.acquisitionDate ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.acquisitionDate && (
                  <p className="mt-1 text-sm text-red-600">{errors.acquisitionDate}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="acquisitionValue"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Valor de Aquisição *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CurrencyDollarIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="acquisitionValue"
                    type="text"
                    value={acquisitionValueDisplay}
                    onChange={e => handleCurrencyChange('acquisitionValue', e.target.value)}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.acquisitionValue ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="0,00"
                  />
                </div>
                {errors.acquisitionValue && (
                  <p className="mt-1 text-sm text-red-600">{errors.acquisitionValue}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="amountPaid"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Valor Pago (Investido) *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CurrencyDollarIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="amountPaid"
                    type="text"
                    value={amountPaidDisplay}
                    onChange={e => handleCurrencyChange('amountPaid', e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0,00"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Quanto já foi efetivamente pago pelo investimento.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Valor Pendente
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CurrencyDollarIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={formatCurrency(Math.max(0, formData.acquisitionValue - formData.amountPaid))}
                    disabled
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Calculado automaticamente (Aquisição - Pago).
                </p>
              </div>

              <div>
                <label
                  htmlFor="currentValue"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Valor Atual *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CurrencyDollarIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="currentValue"
                    type="text"
                    value={currentValueDisplay}
                    onChange={e => handleCurrencyChange('currentValue', e.target.value)}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.currentValue ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="0,00"
                  />
                </div>
                {errors.currentValue && (
                  <p className="mt-1 text-sm text-red-600">{errors.currentValue}</p>
                )}
              </div>
            </div>
          </div>

          {/* Detalhes Específicos */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            {renderTypeSpecificFields()}

            <div className="mt-6">
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                Observações
              </label>
              <textarea
                id="notes"
                value={formData.details.notes || ''}
                onChange={e => handleDetailsChange('notes', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Informações adicionais sobre o ativo..."
              />
            </div>
          </div>

          {/* Anexos - Fotos e Documentos */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <DocumentTextIcon className="h-6 w-6 text-blue-600 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Fotos e Documentos</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Adicione fotos do imóvel e documentos como contrato, escritura, projetos de engenharia
              e arquitetura.
            </p>

            <FileUpload
              files={formData.attachments}
              onFilesChange={files => setFormData(prev => ({ ...prev, attachments: files }))}
              maxFiles={20}
              maxSizePerFile={10}
              showCategories={true}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/assets')}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={createAssetMutation.isLoading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              {createAssetMutation.isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Criando...
                </>
              ) : (
                'Criar Ativo'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateAsset;
